import { DOCUMENT } from '@angular/common';
import { Component, Inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService } from '@auth0/auth0-angular';
import { convertEpochToDateString, GlobalFunctions } from '@global/GlobalFunctions';
import { IAccount, Account } from '@services/accounts.service';
import { AssetsDataService } from '@services/assets-data.service';
import { AuthenticationService, IWebAppUser } from '@services/authentication.service';
import { OrganisationDataService, IOrganisation, Organisation } from '@services/organisation-data.service';
import { HistoricalReport, ReportPDFService } from '@services/report-data.service';
import { combineLatest, takeUntil, distinctUntilChanged, withLatestFrom, filter, Subject, finalize } from 'rxjs';
import { siteSubscriptions } from '@services/siteSubscriptions';
import { Location } from '@angular/common';

@Component({
	selector: 'app-historical-reports',
	templateUrl: './historical-reports.component.html',
	styleUrl: './historical-reports.component.scss'
})
export class HistoricalReportsComponent {

	/** Observable Subject Finalise */
	private finalise = new Subject<void>();

	/** Selected Organisation ID */
	public organisationUID: string;

	/** Selected Organisation */
	public organisation: Organisation;

	/** accountID */
	public accountGUID: string;

	/** selectedAccount */
	public selectedAccount: IAccount;

	/** this user */
	public thisUser: IWebAppUser;

	/** historicalReports */
	public historicalReports: HistoricalReport[] = [];

	/** filteredHistoricalReports */
	public filteredHistoricalReports: HistoricalReport[] = [];

	/** fields */
	public fields: Object = { text: "Name", value: "GeneratedReportID" };

	/**
	 * Default Constructor
	 * @param auth auth 
	 * @param organisationDataService organisationDataService 
	 * @param globalFunctions globalFunctions 
	 * @param router router 
	 * @param route route 
	 * @param siteSubscriptions siteSubscriptions  
	 * @param authenticationService authenticationService 
	 * @param assetsDataService assetsDataService 
	 * @param document document 
	 */
	constructor(
		public auth: AuthService,
		public organisationDataService: OrganisationDataService,
		public globalFunctions: GlobalFunctions,
		public router: Router,
		private route: ActivatedRoute,
		private siteSubscriptions: siteSubscriptions,
		private authenticationService: AuthenticationService,
		private assetsDataService: AssetsDataService,
		private reportsService: ReportPDFService,
		private location: Location,
		@Inject(DOCUMENT) public document: Document
	) {
		/** Get required subscriptions */
		combineLatest([
			this.route.paramMap.pipe(takeUntil(this.finalise), distinctUntilChanged()),
			this.siteSubscriptions.hierarchicalData$.pipe(takeUntil(this.finalise), distinctUntilChanged()),
		]).pipe(
			withLatestFrom(
				this.siteSubscriptions.selectedAccount$.pipe(takeUntil(this.finalise), distinctUntilChanged()),
				this.siteSubscriptions.selectedOrganisation$.pipe(takeUntil(this.finalise), distinctUntilChanged()),
				this.authenticationService.currentUser$.pipe(takeUntil(this.finalise), distinctUntilChanged()),
			),
			// Ensure that subscriptions are not null or undefined
			filter(([[paramMap, hierarchy], selectedAccount, selectedOrganisation, currentUser]) =>
				!!paramMap && !!currentUser && !!hierarchy && !!selectedAccount && !!selectedOrganisation)
		).subscribe({
			next: ([[paramMap, hierarchy], selectedAccount, selectedOrganisation, currentUser]) => {
				this.thisUser = currentUser;
				this.selectedAccount = <IAccount>selectedAccount;
				this.selectedAccount = this.siteSubscriptions.normalizeFeatures(this.selectedAccount) as Account;
				this.organisation = <IOrganisation>selectedOrganisation;

				this.accountGUID = this.selectedAccount.GUID;
				this.organisationUID = this.organisation.Uid;

				this.getReportHistory();
			},
			error: (error) => {
				this.globalFunctions.HandleError(error, "Error: Error in one of the combined subscriptions", false);
			}
		});
	}

	/**
	 * getReportHistory
	 */
	getReportHistory() {
		// Create a local Subject to control the subscription lifecycle
		const fetchDataSubject = new Subject<void>();
		this.globalFunctions.displaySpinner(true);

		this.reportsService.getOrganisationReportHistory$(this.accountGUID, this.organisationUID).pipe(
			takeUntil(fetchDataSubject), distinctUntilChanged(),
			filter((history) => !!history),
			finalize(() => {
				// Ensure the subscription is terminated and the subject is completed
				fetchDataSubject.next();
				fetchDataSubject.complete();
				this.globalFunctions.displaySpinner(false);
			})
		).subscribe({
			next: (history) => {
				this.historicalReports = history;
				this.historicalReports = this.historicalReports.map((hr: HistoricalReport) => (
					{
						...hr,
						FileName: `${hr.FileName.replaceAll("/", "").replaceAll(":", "")}${hr.FileType.split("(")[1].replace(")", "")}`
					}
				));
				this.filteredHistoricalReports = this.historicalReports;
			},
			error: (error) => {
				if (error.status != 404) {
					console.error("this.reportsService.getOrganisationReportHistory$:", error);
				}
			},
		});
	}

	/**
	 * convertEpoch
	 * @param epoch
	 * @returns
	 */
	convertEpoch(epoch: number): string {
		/** Use the Global Function */
		return convertEpochToDateString(epoch);
	}

	/**
	 * getFile
	 * @param report report 
	 */
	getFile(report: HistoricalReport, download: boolean = false) {
		let mimeType: string = "";
		if (report.FileType.toLowerCase().includes("pdf")) {
			mimeType = "application/pdf";
		}
		else if (report.FileType.toLowerCase().includes("xlsx")) {
			mimeType = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
			download = true;
		}

		const byteCharacters = atob(report.Base64); // Decode Base64 string
		const byteNumbers = new Array(byteCharacters.length);

		for (let i = 0; i < byteCharacters.length; i++) {
			byteNumbers[i] = byteCharacters.charCodeAt(i);
		}

		const byteArray = new Uint8Array(byteNumbers);
		const blob = new Blob([byteArray], { type: mimeType });

		if (!download) {
			this.openBlob(blob);
		} else {
			this.downloadBlob(blob, report.FileName);
		}
	}

	/**
	 * openBlob
	 * @param blob blob  
	 */
	openBlob(blob: Blob) {
		const url = window.URL.createObjectURL(blob);
		window.open(url);
	}

	/**
	 * downloadBlob
	 * @param blob blob 
	 * @param filename filename 
	 */
	downloadBlob(blob: Blob, filename: string) {
		const url = window.URL.createObjectURL(blob);

		const a = document.createElement('a');
		a.href = url;
		a.download = filename;
		a.click();

		window.URL.revokeObjectURL(url);
	}

	/**
	 * onFilteringReportHistory
	 * @param event
	 */
	onFilteringReportHistory(event: any): void {
		event.cancel = true;
		const query = event.target.value.toLowerCase();
		this.filteredHistoricalReports = this.historicalReports.filter((report: HistoricalReport) => {
			const name: string = `${report.Name}`.toLowerCase();
			const description: string = `${report.Description}`.toLowerCase() || '';
			const fileType: string = `${report.FileType}`.toLowerCase();
			const dateGenerated: string = `${this.convertEpoch(report.GeneratedAt)}`.toLowerCase() || '';

			const combinedStr = `${name} ${description} ${fileType} ${dateGenerated}`;
			return combinedStr.includes(query);
		});
	}

	/**
	 * Back Button
	 */
	backClicked() {
		this.location.back();
	}
}
